import { useClerk } from "@clerk/nextjs";
import { IconBuilding, IconChevronDown, IconLogout2, IconSettings, IconSwitch, IconUser } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { forwardRef } from "react";

import { useAuthorization } from "~/contexts/authorizationProvider";
import { useOrg } from "~/contexts/orgProvider";
import { useUser } from "~/contexts/userProvider";
import { SystemPermission } from "~/shared/types/RBACPermission";
import {
  Avatar,
  Button,
  Divider,
  Flex,
  Modal,
  mantineColorRamps,
  Popover,
  type SizeValue,
  showNotification,
  Text,
  Tooltip,
  tokens,
  UnstyledButton,
  type UnstyledButtonProps,
  useClickOutside,
  useDisclosure,
} from "~/ui";
import { api } from "~/utils/api";

import { OrganizationSwitcher, SelectedOrganization } from "../OrganizationSwitcher";

import styles from "./AccountActions.module.css";
import { AccountActionsNavItem } from "./AccountActionsNavItem";

type Nullable<T> = T | null | undefined;

type AccountActionsProps = {
  closeNavbar: () => void;
  toggleSettingsNavbar: () => void;
  /** The compact navigation shows the org avatar alone, named by the org in a tooltip. */
  compact?: boolean;
};

type AccountActionsAvatarProps = {
  imageUrl: Nullable<string>;
  label: Nullable<string>;
  placeholderIcon: JSX.Element;
  size?: SizeValue;
};

const AccountActionsAvatar = (props: AccountActionsAvatarProps): JSX.Element => {
  return (
    <Avatar alt={props.label ?? undefined} color="primary" radius="sm" size={props.size ?? "md"} src={props.imageUrl}>
      {props.placeholderIcon}
    </Avatar>
  );
};

type CompactAccountButtonProps = UnstyledButtonProps & {
  tooltipDisabled: boolean;
};

/**
 * The org avatar alone, for the compact navigation, on a target that fills on hover, focus and while the account
 * popover is open. The tooltip wraps the button itself so the popover target's props and the button's own click
 * both reach the button.
 */
const CompactAccountButton = forwardRef<HTMLButtonElement, CompactAccountButtonProps>(function CompactAccountButton(
  { tooltipDisabled, children, ...props },
  ref,
) {
  return (
    <Tooltip
      arrowSize={6}
      disabled={tooltipDisabled}
      events={{ hover: true, focus: true, touch: false }}
      label="Account menu"
      offset={12}
      position="right"
      withArrow
    >
      <UnstyledButton {...props} aria-label="Account menu" className={styles.compactAccountButton} ref={ref}>
        {children}
      </UnstyledButton>
    </Tooltip>
  );
});

export const AccountActions = (props: AccountActionsProps): JSX.Element => {
  const { hasPermission } = useAuthorization();
  const { orgIcon, orgName } = useOrg();
  const { userFirstName, userLastName, userProfileImage } = useUser();
  const userMembershipsQuery = api.rbac.findUserOrgs.useQuery();
  const queryClient = useQueryClient();

  const { signOut } = useClerk();
  const [opened, { close, toggle }] = useDisclosure(false);
  const [modalOpened, setModalOpened] = useDisclosure(false);
  const popoverTargetRef = useClickOutside(close);

  const onOrgSwitch = (org: SelectedOrganization) => {
    showNotification({
      autoClose: 5000,
      color: "green",
      title: "Organization Selected",
      message: `Switched to Organization: ${org.name}`,
    });
    setModalOpened.close();
  };

  const handleSignout = async () => {
    await queryClient.cancelQueries();
    queryClient.removeQueries();
    await signOut();
    window.location.assign("/sign-in");
  };

  const handleMenuItemClick = (cb?: () => void, closeNavbar = true) => {
    if (!opened) {
      return;
    }
    if (!!cb) {
      cb();
    }
    if (closeNavbar) {
      props.closeNavbar();
    }
    close();
  };

  const hasViewSettingsPermission = hasPermission([SystemPermission.viewSettings]);
  const renderOrganizationSwitcher = !!userMembershipsQuery.data?.length;
  const userFullName = `${userFirstName} ${userLastName}`;

  return (
    <Flex align={props.compact ? "center" : undefined} justify={props.compact ? "center" : undefined} ref={popoverTargetRef} w="100%">
      <Popover opened={opened} position="bottom-start" shadow="md" transitionProps={{ transition: "pop" }} width={250} withArrow>
        <Popover.Target>
          {props.compact ? (
            <CompactAccountButton onClick={toggle} tooltipDisabled={opened}>
              <AccountActionsAvatar imageUrl={orgIcon} label={orgName} placeholderIcon={<IconBuilding />} />
            </CompactAccountButton>
          ) : (
            <Button px="xs" compact onClick={toggle} radius="xs" className={styles.accountButton} variant="subtle" fullWidth>
              <AccountActionsAvatar imageUrl={orgIcon} label={orgName} placeholderIcon={<IconBuilding />} />
              <div>
                <Flex direction="row" align="center">
                  <Text pl="sm" fw="bold" fz="md" truncate maw={{ base: "100%", md: "120px" }}>
                    {orgName}
                  </Text>
                  <IconChevronDown color={mantineColorRamps.gray[7]} size={14} style={{ marginLeft: 6 }} />
                </Flex>
                <Text color={mantineColorRamps.gray[7]} pl="sm" size="xs" truncate maw={{ base: "100%", md: "130px" }}>
                  {userFullName}
                </Text>
              </div>
            </Button>
          )}
        </Popover.Target>
        <Popover.Dropdown style={{ padding: 0 }}>
          <Flex direction="column">
            <div
              style={{
                padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
              }}
            >
              <Flex direction="column">
                <Flex align="center" py="sm">
                  <AccountActionsAvatar imageUrl={orgIcon} label={orgName} placeholderIcon={<IconBuilding />} size="sm" />
                  <Text pl="sm" fw="bold" fz="sm">
                    {orgName}
                  </Text>
                </Flex>
                {hasViewSettingsPermission && (
                  <AccountActionsNavItem
                    icon={IconSettings}
                    label="Settings"
                    onClick={() => handleMenuItemClick(props.toggleSettingsNavbar, false)}
                  />
                )}
                <Divider my="sm" />
                <Flex align="center" py="sm">
                  <AccountActionsAvatar imageUrl={userProfileImage} label={userProfileImage} placeholderIcon={<IconUser />} size="sm" />
                  <Text pl="sm" fw="bold" fz="sm">
                    {userFullName}
                  </Text>
                </Flex>
                <AccountActionsNavItem
                  icon={IconUser}
                  label="Profile"
                  url="/settings/account/manage"
                  onClick={() => handleMenuItemClick()}
                />
                {renderOrganizationSwitcher && (
                  <AccountActionsNavItem
                    icon={IconSwitch}
                    label="Switch Account"
                    onClick={() => handleMenuItemClick(setModalOpened.open, false)}
                  />
                )}
                <AccountActionsNavItem icon={IconLogout2} label="Log Out" onClick={() => handleMenuItemClick(handleSignout)} />
              </Flex>
            </div>
          </Flex>
        </Popover.Dropdown>
      </Popover>
      <Modal opened={modalOpened} onClose={() => setModalOpened.close()} title="Select Organization" centered>
        <OrganizationSwitcher onSwitch={onOrgSwitch} />
      </Modal>
    </Flex>
  );
};
